import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Handle } from "react-flow-renderer";
import { ContentCopy, DeleteOutline } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { getTokens, brandAlpha, shadeBrand, INK } from "../../../styles/tokens";

/* Casca comum dos nós do editor de fluxo: cartão do painel (superfície, borda,
 * raio 8, sombra suave), cabeçalho com o ícone do tipo num quadradinho de cor,
 * ações discretas e conectores na cor da marca. A cor por tipo é identidade do
 * nó (como cor de dado) e só pinta o ícone. */

// Centro vertical do cabeçalho: é onde entram e saem as conexões de nó simples.
export const HEAD_Y = 23;

export const useNodeStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const dark = t.mode === "dark";
  const strong = dark ? t.text : INK[800];
  return {
    root: {
      position: "relative",
      boxSizing: "border-box",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.soft,
      fontFamily: t.font,
      color: t.text,
      textAlign: "left",
      transition: `border-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
      "&:hover": { borderColor: t.lineStrong },
    },
    head: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 8px 8px 12px",
      minWidth: 0,
    },
    tile: {
      flex: "none",
      width: 26,
      height: 26,
      borderRadius: t.radius.badge,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": { width: 15, height: 15, fontSize: 15 },
      "& img": { width: 15, height: 15, objectFit: "contain" },
    },
    title: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.3,
      color: strong,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    actions: { flex: "none", display: "flex", alignItems: "center", gap: 2 },
    action: {
      appearance: "none",
      border: 0,
      margin: 0,
      padding: 0,
      width: 22,
      height: 22,
      borderRadius: t.radius.badge,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "transparent",
      color: t.textMuted,
      cursor: "pointer",
      transition: `background-color ${t.motion.fast}, color ${t.motion.fast}`,
      "& svg": { width: 13, height: 13, fontSize: 13 },
      "&:hover": { backgroundColor: t.tint.soft, color: t.brand },
      "&:focus-visible": { outline: `2px solid ${t.brand}`, outlineOffset: 1 },
    },
    actionDanger: {
      "&:hover": { backgroundColor: t.status.down.bg, color: t.status.down.fg },
    },
    body: {
      padding: "0 12px 12px",
      fontSize: 12,
      lineHeight: 1.45,
      color: t.textSecondary,
      overflowWrap: "anywhere",
      minWidth: 0,
    },
    // valor em destaque dentro do nó (fila, tag, atendente, integração…)
    slot: {
      padding: "6px 8px",
      borderRadius: t.radius.badge,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      color: strong,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      minWidth: 0,
      "& + &": { marginTop: 4 },
    },
    slotRow: { display: "flex", alignItems: "center", gap: 6, minWidth: 0, "& svg": { width: 14, height: 14, fontSize: 14, flex: "none", color: t.textMuted } },
    ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
    clamp3: {
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    hint: { marginTop: 6, fontSize: 11, lineHeight: 1.4, color: t.textMuted },
    savedOk: { fontSize: 11.5, fontWeight: 600, lineHeight: 1.4, color: t.status.ok.fg },
    media: { display: "block", width: "100%", borderRadius: t.radius.badge, border: `1px solid ${t.line}`, backgroundColor: t.muted },
    // linha com saída própria (opção de menu, ramo do randomizador, lido/não lido)
    outRow: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      minHeight: 24,
      padding: "0 2px 0 8px",
      marginTop: 4,
      borderRadius: t.radius.badge,
      backgroundColor: t.muted,
      color: strong,
      fontSize: 11.5,
      fontWeight: 500,
      minWidth: 0,
      "& > span": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, paddingRight: 6 },
    },
    handle: {
      // duas classes: vence o CSS padrão do react-flow seja qual for a ordem das folhas
      "&.react-flow__handle": {
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `2px solid ${t.paper}`,
        backgroundColor: t.brand,
        color: t.brandOn,
        boxShadow: `0 0 0 1px ${brandAlpha(t.brand, 0.35)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxSizing: "border-box",
        "& svg": { width: 8, height: 8, pointerEvents: "none" },
      },
    },
    handleOk: {
      "&.react-flow__handle": {
        backgroundColor: t.semantic.success,
        color: "#ffffff",
        boxShadow: `0 0 0 1px ${brandAlpha(t.semantic.success, 0.35)}`,
      },
    },
    handleDown: {
      "&.react-flow__handle": {
        backgroundColor: t.semantic.error,
        color: "#ffffff",
        boxShadow: `0 0 0 1px ${brandAlpha(t.semantic.error, 0.35)}`,
      },
    },
  };
});

const cx = (...parts) => parts.filter(Boolean).join(" ");

/** Cores do quadradinho do ícone a partir da cor de identidade do tipo de nó. */
export const useTypeTone = (color) => {
  const theme = useTheme();
  const dark = theme.palette.type === "dark";
  return {
    backgroundColor: brandAlpha(color, dark ? 0.24 : 0.12),
    color: dark ? shadeBrand(color, 0.4) : color,
  };
};

/** Duplicar/excluir: as mesmas ações de sempre (useNodeStorage), em botões discretos. */
export const NodeActions = ({ id }) => {
  const classes = useNodeStyles();
  const storageItems = useNodeStorage();
  return (
    <div className={cx(classes.actions, "nodrag")}>
      <button
        type="button"
        className={classes.action}
        title="Duplicar bloco"
        aria-label="Duplicar bloco"
        onClick={() => {
          storageItems.setNodesStorage(id);
          storageItems.setAct("duplicate");
        }}
      >
        <ContentCopy />
      </button>
      <button
        type="button"
        className={cx(classes.action, classes.actionDanger)}
        title="Excluir bloco"
        aria-label="Excluir bloco"
        onClick={() => {
          storageItems.setNodesStorage(id);
          storageItems.setAct("delete");
        }}
      >
        <DeleteOutline />
      </button>
    </div>
  );
};

/** Cabeçalho do nó: ícone do tipo, título e ações (passe `id` para ter duplicar/excluir). */
export const NodeHead = ({ color, icon, title, id, children }) => {
  const classes = useNodeStyles();
  const tone = useTypeTone(color);
  return (
    <div className={classes.head}>
      <span className={classes.tile} style={tone} aria-hidden="true">
        {icon}
      </span>
      <span className={classes.title} title={typeof title === "string" ? title : undefined}>
        {title}
      </span>
      {children}
      {id !== undefined && <NodeActions id={id} />}
    </div>
  );
};

/**
 * Cartão do nó. `width` é a largura total (padrão 200); `id` liga as ações.
 * Os conectores (`NodeHandle`) entram como filhos, em qualquer ponto.
 */
export const NodeCard = ({ color, icon, title, id, width = 200, bodyClassName, style, children }) => {
  const classes = useNodeStyles();
  return (
    <div className={cx(classes.root, "kp-flow-node")} style={{ width, ...style }}>
      <NodeHead color={color} icon={icon} title={title} id={id} />
      <div className={cx(classes.body, bodyClassName)}>{children}</div>
    </div>
  );
};

const Arrow = () => (
  <svg viewBox="0 0 10 10" aria-hidden="true">
    <path d="M3.2 1.5 L6.8 5 L3.2 8.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Conector no padrão do editor. `type` = "target" (esquerda) | "source" (direita);
 * `top` em px ou % (padrão: altura do cabeçalho); `offset` = quanto sai da borda
 * do elemento posicionado mais próximo; `tone` = "ok" | "down" para saídas com sentido.
 */
export const NodeHandle = ({ type, id, top = HEAD_Y, offset = -9, tone, isConnectable, style }) => {
  const classes = useNodeStyles();
  const isTarget = type === "target";
  const idProp = id !== undefined ? { id } : {};
  return (
    <Handle
      type={type}
      position={isTarget ? "left" : "right"}
      {...idProp}
      className={cx(classes.handle, tone === "ok" && classes.handleOk, tone === "down" && classes.handleDown)}
      style={{ top, ...(isTarget ? { left: offset } : { right: offset }), ...style }}
      isConnectable={isConnectable}
    >
      <Arrow />
    </Handle>
  );
};
